/**
 * Netskope — pull web and app events from a Netskope tenant (no endpoint agent), and list the
 * MCP servers people are reaching so they can be registered on the gateway and exported as
 * connector URLs for the Claude admin.
 *
 * Only traffic Netskope steers and decrypts is visible; MCP calls made by Claude's cloud
 * connectors never pass through it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '../components/ui';
import { usePermission } from '../hooks/usePermission';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface Props {
  apiUrl: string;
}

interface NsConfig {
  tenantUrl: string;
  hasToken: boolean;
  enabled: boolean;
  syncIntervalMin: number;
  lastSyncAt: number | null;
  lastStatus: string | null;
  lastCounts: { events: number; matched: number; mcpUrlHits: number; pages: number } | null;
}

interface McpRow {
  host: string;
  path: string;
  url: string;
  hits: number;
  users: number;
  lastSeen: string;
  provider: string | null;
  suggestedSlug: string;
  suggestedName: string;
  registeredSlug: string | null;
}

async function asJson(res: Response): Promise<any> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || `Request failed (${res.status})`);
  return data;
}

const send = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

const NetskopeSettings: React.FC<Props> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const canWrite = usePermission('manage:settings');
  const [config, setConfig] = useState<NsConfig | null>(null);
  const [form, setForm] = useState({ tenantUrl: 'https://', apiToken: '', syncIntervalMin: 15, enabled: true });
  const [servers, setServers] = useState<McpRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    try {
      const c: NsConfig | null = (await asJson(await fetch(`${apiUrl}/admin/netskope/config`))).config;
      setConfig(c);
      if (c) setForm({ tenantUrl: c.tenantUrl, apiToken: '', syncIntervalMin: c.syncIntervalMin, enabled: c.enabled });
    } catch (e: any) {
      setError(e.message);
    }
  }, [apiUrl]);

  const loadServers = useCallback(async () => {
    try {
      setServers((await asJson(await fetch(`${apiUrl}/admin/discovery/mcp-servers`))).servers);
    } catch {
      /* nothing discovered yet */
    }
  }, [apiUrl]);

  useEffect(() => {
    void loadConfig();
    void loadServers();
  }, [loadConfig, loadServers]);

  const run = async (key: string, fn: () => Promise<string>) => {
    setBusy(key);
    setError(null);
    setNote(null);
    try {
      setNote(await fn());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const save = () =>
    run('save', async () => {
      const r = await asJson(await fetch(`${apiUrl}/admin/netskope/config`, send('PUT', form)));
      setConfig(r.config);
      setForm((f) => ({ ...f, apiToken: '' }));
      return t('pages.netskopeSettings.saved');
    });

  const test = () =>
    run('test', async () => {
      await asJson(await fetch(`${apiUrl}/admin/netskope/test`, send('POST', form.apiToken ? form : {})));
      return t('pages.netskopeSettings.connected');
    });

  const sync = () =>
    run('sync', async () => {
      const r = await asJson(await fetch(`${apiUrl}/admin/netskope/sync`, send('POST', {})));
      setConfig(r.config);
      await loadServers();
      return t('pages.netskopeSettings.synced', { events: r.result.events, mcp: r.result.mcpUrlHits });
    });

  const register = (s: McpRow) =>
    run(`reg-${s.url}`, async () => {
      const r = await asJson(await fetch(`${apiUrl}/admin/discovery/mcp-servers/register`, send('POST', { host: s.host, path: s.path })));
      await loadServers();
      return t('pages.netskopeSettings.registered', { slug: r.slug });
    });

  const exportCsv = () =>
    run('export', async () => {
      const res = await fetch(`${apiUrl}/admin/discovery/mcp-servers/export`);
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = 'agenti5-connector-urls.csv';
      a.click();
      URL.revokeObjectURL(url);
      return t('pages.netskopeSettings.exported');
    });

  const registeredCount = servers.filter((s) => s.registeredSlug).length;

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>{t('pages.netskopeSettings.title')}</h1>
          <p className="subtitle">{t('pages.netskopeSettings.subtitle')}</p>
        </div>
      </div>

      {error && (
        <div className="notice error">
          <Icon name="warning" /> {error}
        </div>
      )}
      {note && (
        <div className="notice">
          <Icon name="checkCircle" /> {note}
        </div>
      )}

      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">
          <Icon name="server" /> {t('pages.netskopeSettings.connection')}
          {config?.lastStatus && (
            <span className={`badge ${config.lastStatus === 'ok' ? 'badge-success' : 'badge-error'}`} style={{ marginLeft: 8 }}>
              {config.lastStatus === 'ok' ? t('pages.netskopeSettings.inSync') : config.lastStatus}
            </span>
          )}
        </div>
        <div className="ent-card-body" style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
          <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
            <span style={{ color: 'var(--text-2)' }}>{t('pages.netskopeSettings.tenantUrl')}</span>
            <input value={form.tenantUrl} disabled={!canWrite} placeholder="https://yourtenant.goskope.com" onChange={(e) => setForm({ ...form, tenantUrl: e.target.value })} />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
            <span style={{ color: 'var(--text-2)' }}>{t('pages.netskopeSettings.apiToken')}</span>
            <input
              type="password"
              autoComplete="new-password"
              value={form.apiToken}
              disabled={!canWrite}
              placeholder={config?.hasToken ? t('pages.netskopeSettings.tokenKept') : ''}
              onChange={(e) => setForm({ ...form, apiToken: e.target.value })}
            />
            <span style={{ fontSize: 10, color: 'var(--text-2)' }}>{t('pages.netskopeSettings.tokenHint')}</span>
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
            <span style={{ color: 'var(--text-2)' }}>{t('pages.netskopeSettings.interval')}</span>
            <input type="number" min={5} max={1440} value={form.syncIntervalMin} disabled={!canWrite} onChange={(e) => setForm({ ...form, syncIntervalMin: Number(e.target.value) })} />
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
            <input type="checkbox" checked={form.enabled} disabled={!canWrite} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
            {t('pages.netskopeSettings.enabled')}
          </label>
        </div>
        <div className="ent-toolbar" style={{ padding: '0 16px 16px' }}>
          <button className="ent-btn primary" disabled={!canWrite || busy !== null} onClick={save}>{t('pages.netskopeSettings.save')}</button>
          <button className="ent-btn" disabled={!canWrite || busy !== null} onClick={test}>{t('pages.netskopeSettings.test')}</button>
          <button className="ent-btn" disabled={!canWrite || busy !== null || !config} onClick={sync}>{t('pages.netskopeSettings.syncNow')}</button>
          {config?.lastSyncAt && (
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
              {t('pages.netskopeSettings.lastSync', { when: parseServerDate(new Date(config.lastSyncAt).toISOString()).toLocaleString() })}
            </span>
          )}
        </div>
      </div>

      <div className="ent-card">
        <div className="ent-card-head" style={{ justifyContent: 'space-between' }}>
          <span>
            <Icon name="server" /> {t('pages.netskopeSettings.discovered')} ({servers.length})
          </span>
          <button className="ent-btn" disabled={registeredCount === 0 || busy !== null} onClick={exportCsv}>
            {t('pages.netskopeSettings.exportCsv')}
          </button>
        </div>
        <div className="ent-card-body" style={{ fontSize: 12, color: 'var(--text-2)' }}>{t('pages.netskopeSettings.limit')}</div>
        <div className="ent-table-wrap scroll">
          <table className="ent-table">
            <thead>
              <tr>
                <th>{t('pages.netskopeSettings.url')}</th>
                <th>{t('pages.netskopeSettings.provider')}</th>
                <th style={{ width: 80 }}>{t('pages.netskopeSettings.users')}</th>
                <th style={{ width: 80 }}>{t('pages.netskopeSettings.hits')}</th>
                <th style={{ width: 170 }}>{t('pages.netskopeSettings.lastSeen')}</th>
                <th style={{ width: 160 }}></th>
              </tr>
            </thead>
            <tbody>
              {servers.length === 0 ? (
                <tr><td colSpan={6} className="ent-empty">{t('pages.netskopeSettings.none')}</td></tr>
              ) : (
                servers.map((s) => (
                  <tr key={s.url}>
                    <td>{s.url}</td>
                    <td>{s.provider || '—'}</td>
                    <td>{s.users}</td>
                    <td>{s.hits}</td>
                    <td>{parseServerDate(s.lastSeen).toLocaleString()}</td>
                    <td>
                      {s.registeredSlug ? (
                        <span className="badge badge-success">/mcp-gw/{s.registeredSlug}</span>
                      ) : (
                        <button className="ent-btn" disabled={!canWrite || busy !== null} onClick={() => register(s)}>
                          {t('pages.netskopeSettings.register')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NetskopeSettings;
